import { Box, Button, HStack, Icon, Text, chakra } from "@chakra-ui/react";
import React from "react";
import { FiEdit2, FiMessageSquare } from "react-icons/fi";
import type {
  AnswersByQuestion,
  ContentItem,
  LayoutBlock,
  PlaceholderValues,
} from "./layout/documentLayout";
import { getQuestionType, type DocumentLayoutType } from "./question-types/registry";

// One question of the review preview: the print-style view of its type, plus the review chrome
// around it (comment outline + count badge, highlight when a comment is clicked, hover actions).
// Everything interactive is optional and only rendered when its prop is given, so the read-only
// screen and the editing screen share this component.

export interface QuestionBlockProps {
  answers: AnswersByQuestion;
  block: LayoutBlock;
  byId: Map<number, ContentItem>;
  /** Open comments plus unsent drafts on this question; > 0 draws the orange outline + badge. */
  commentCount: number;
  /** Replaces the view while the question is being edited inline (hover actions are hidden). */
  editor?: React.ReactNode;
  /** Extra hover actions, rendered after the built-in ones. */
  extraActions?: React.ReactNode;
  /** An unsent draft comment on this question (orange outline even when `commentCount` is 0). */
  hasDraft?: boolean;
  /** Keep the hover actions visible (e.g. while a popover anchored to this block is open). */
  isActive?: boolean;
  /** Answer edited in this session: shows a small "Edited" marker. */
  isEdited?: boolean;
  /** Briefly true after a comment of this question was clicked in the side panel. */
  isHighlighted: boolean;
  layoutType: DocumentLayoutType;
  placeholders: PlaceholderValues;
  onComment?: () => void;
  onEdit?: () => void;
}

const BlockRoot = chakra("div", {
  baseStyle: {
    borderRadius: "4px",
    position: "relative",
    // Scrolled into view from the side panel: keep it clear of the pane edges.
    scrollMarginBlock: "96px",
    transitionDuration: "0.3s",
    transitionProperty: "background-color, box-shadow, outline-color",
  },
});

function HoverAction(props: { icon: React.ElementType; label: string; onClick: () => void }) {
  return (
    <Button
      bg="white"
      borderColor="gray.200"
      borderWidth={1}
      boxShadow="sm"
      fontWeight="medium"
      h={6}
      iconSpacing={1}
      leftIcon={<Icon as={props.icon} boxSize={3} />}
      px={2}
      size="xs"
      variant="outline"
      onClick={props.onClick}
    >
      {props.label}
    </Button>
  );
}

const QuestionBlock = React.forwardRef<HTMLDivElement, QuestionBlockProps>(function QuestionBlock(
  props,
  ref
) {
  const { View } = getQuestionType(props.block.question.itemType);
  const isFlagged = props.commentCount > 0 || props.hasDraft === true;
  const isEditing = props.editor !== undefined && props.editor !== null;

  const actions: React.ReactNode[] = [];
  if (!isEditing) {
    if (props.onComment !== undefined) {
      actions.push(
        <HoverAction
          key="comment"
          icon={FiMessageSquare}
          label="Comment"
          onClick={props.onComment}
        />
      );
    }
    if (props.onEdit !== undefined) {
      actions.push(<HoverAction key="edit" icon={FiEdit2} label="Edit" onClick={props.onEdit} />);
    }
    if (props.extraActions !== undefined && props.extraActions !== null) {
      actions.push(<React.Fragment key="extra">{props.extraActions}</React.Fragment>);
    }
  }
  const hasActions = actions.length > 0;

  return (
    <BlockRoot
      ref={ref}
      _hover={hasActions ? { bg: "blackAlpha.50" } : undefined}
      bg={props.isHighlighted ? "blue.50" : undefined}
      boxShadow={props.isHighlighted ? "0 0 0 3px var(--chakra-colors-blue-300)" : undefined}
      data-question-id={props.block.question.id}
      outline="2px solid"
      outlineColor={isFlagged ? "orange.400" : "transparent"}
      outlineOffset="4px"
      role="group"
    >
      {isEditing ? (
        props.editor
      ) : (
        <View
          answers={props.answers}
          block={props.block}
          byId={props.byId}
          layoutType={props.layoutType}
          placeholders={props.placeholders}
        />
      )}

      {props.isEdited === true && !isEditing && (
        <Text
          bg="white"
          bottom="-14px"
          color="blue.500"
          fontSize="10px"
          fontWeight="semibold"
          position="absolute"
          px={1}
          right={0}
        >
          Edited
        </Text>
      )}

      {props.commentCount > 0 && (
        <Box
          alignItems="center"
          aria-label={`${props.commentCount} comment${props.commentCount === 1 ? "" : "s"}`}
          bg="orange.400"
          borderRadius="full"
          color="white"
          display="flex"
          fontSize="10px"
          fontWeight="bold"
          h="18px"
          justifyContent="center"
          minW="18px"
          position="absolute"
          px={1}
          right="-14px"
          top="-14px"
          zIndex={1}
        >
          {props.commentCount}
        </Box>
      )}

      {hasActions && (
        <HStack
          _groupFocusWithin={{ opacity: 1, pointerEvents: "auto" }}
          _groupHover={{ opacity: 1, pointerEvents: "auto" }}
          opacity={props.isActive === true ? 1 : 0}
          pointerEvents={props.isActive === true ? "auto" : "none"}
          position="absolute"
          right={props.commentCount > 0 ? "10px" : "-6px"}
          spacing={1}
          top="-16px"
          transition="opacity 0.15s"
          zIndex={2}
        >
          {actions}
        </HStack>
      )}
    </BlockRoot>
  );
});

export default QuestionBlock;
